import { CheckCircle, Copy, Search } from 'lucide-react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { facebookAdsSearchTargeting } from 'sprites-types/workflow/tools/facebookAdsSearchTargeting'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function FacebookAdsSearchTargetingDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsSearchTargeting, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const targetingOptions = result.targetingOptions

  if (targetingOptions.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">
            No targeting options found for {result.searchType}
          </div>
        </div>
      </div>
    )
  }

  const formatAudienceSize = (
    lower: number | null | undefined,
    upper: number | null | undefined
  ): string => {
    if (!lower && !upper) return 'N/A'
    if (lower && upper) {
      if (lower === upper) {
        return formatNumber(lower)
      }
      return `${formatNumber(lower)} - ${formatNumber(upper)}`
    }
    if (lower) return `${formatNumber(lower)}+`
    if (upper) return `Up to ${formatNumber(upper)}`
    return 'N/A'
  }

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`
    }
    return num.toString()
  }

  const getSearchTypeLabel = (searchType: string): string => {
    switch (searchType) {
      case 'interests':
        return 'Interests'
      case 'behaviors':
        return 'Behaviors'
      case 'demographics':
        return 'Demographics'
      case 'life_events':
        return 'Life Events'
      case 'locales':
        return 'Locales'
      default:
        return searchType
    }
  }

  const contextActions: ContextAction[] = [
    {
      id: 'copy-all-ids',
      label: 'Copy All Targeting IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: () =>
        navigator.clipboard.writeText(targetingOptions.map((option) => option.id).join('\n')),
    },
    {
      id: 'copy-all-names',
      label: 'Copy All Names',
      icon: <Copy className="h-4 w-4" />,
      onClick: () =>
        navigator.clipboard.writeText(targetingOptions.map((option) => option.name).join('\n')),
    },
    {
      id: 'copy-ids-json',
      label: 'Copy IDs as JSON Array',
      icon: <Copy className="h-4 w-4" />,
      onClick: () =>
        navigator.clipboard.writeText(
          JSON.stringify(
            targetingOptions.map((option) => ({ id: option.id, name: option.name })),
            null,
            2
          )
        ),
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center justify-between">
              <div className="mb-2 flex items-center gap-2">
                <FaFacebook className="h-4 w-4 text-[#0064e0]" />
                <div className="text-sm font-semibold text-gray-900">
                  {result.totalResults} {getSearchTypeLabel(result.searchType)} Targeting
                  {result.totalResults === 1 ? ' Option' : ' Options'} Found
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {targetingOptions.map((option) => (
                <div
                  key={option.id}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900">{option.name}</div>
                    <button
                      onClick={() => navigator.clipboard.writeText(option.id)}
                      className="rounded px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200"
                      title="Copy ID"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="mb-1 text-xs text-gray-500">ID: {option.id}</div>
                  {option.path && option.path.length > 0 && (
                    <div className="mb-1 text-xs text-gray-500">
                      Path: {option.path.join(' > ')}
                    </div>
                  )}
                  {option.description && (
                    <div className="mb-1 text-xs text-gray-600">{option.description}</div>
                  )}
                  {(option.audience_size_lower_bound || option.audience_size_upper_bound) && (
                    <div className="text-xs text-gray-500">
                      Audience Size:{' '}
                      {formatAudienceSize(
                        option.audience_size_lower_bound,
                        option.audience_size_upper_bound
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsSearchTargeting(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsSearchTargetingToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsSearchTargeting, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const targetingOptions = result.targetingOptions

  if (targetingOptions.length === 0) {
    return `## Meta Ad Targeting Options (${result.searchType})

No targeting options found.

---
`
  }

  const formatAudienceSize = (
    lower: number | null | undefined,
    upper: number | null | undefined
  ): string => {
    if (!lower && !upper) return 'N/A'
    if (lower && upper) {
      if (lower === upper) {
        return lower.toLocaleString()
      }
      return `${lower.toLocaleString()} - ${upper.toLocaleString()}`
    }
    if (lower) return `${lower.toLocaleString()}+`
    if (upper) return `Up to ${upper.toLocaleString()}`
    return 'N/A'
  }

  const getSearchTypeLabel = (searchType: string): string => {
    switch (searchType) {
      case 'interests':
        return 'Interests'
      case 'behaviors':
        return 'Behaviors'
      case 'demographics':
        return 'Demographics'
      case 'life_events':
        return 'Life Events'
      case 'locales':
        return 'Locales'
      default:
        return searchType
    }
  }

  const optionsList = targetingOptions
    .map(
      (option) =>
        `- **${option.name}** (ID: ${option.id})${option.path && option.path.length > 0 ? `\n  - Path: ${option.path.join(' > ')}` : ''}${option.description ? `\n  - Description: ${option.description}` : ''}${option.audience_size_lower_bound || option.audience_size_upper_bound ? `\n  - Audience Size: ${formatAudienceSize(option.audience_size_lower_bound, option.audience_size_upper_bound)}` : ''}`
    )
    .join('\n\n')

  return `## Meta Ad Targeting Options: ${getSearchTypeLabel(result.searchType)} (${result.totalResults})

${optionsList}

---
`
}

export const FacebookAdsSearchTargetingDisplay = createWorkflowToolComponent(
  FacebookAdsSearchTargetingDisplayComponent,
  downloadFacebookAdsSearchTargeting,
  facebookAdsSearchTargetingToMarkdown
)
